import React, { useState } from "react";

type ScoreGridProps = {
  players: string[];
  onReset: () => void;
};

const ScoreGrid: React.FC<ScoreGridProps> = ({ players, onReset }) => {
  const [scores, setScores] = useState<number[][]>(() => {
    // Create an array of 11 rows filled with zeros
    return Array(11)
      .fill(0)
      .map(() => Array(players.length).fill(0));
  });

  const handleScoreChange = (
    rowIndex: number,
    playerIndex: number,
    value: string
  ) => {
    const newScore = value === "" ? 0 : parseInt(value, 10);

    // Check if it's a valid number
    if (isNaN(newScore)) return;

    const newScores = [...scores];
    newScores[rowIndex][playerIndex] = newScore;
    setScores(newScores);
  };

  const calculateTotal = (playerIndex: number) => {
    return scores.reduce((sum, row) => sum + (row[playerIndex] || 0), 0);
  };

  // Determine who's winning (highest score)
  const determineWinner = () => {
    const totals = players.map((_, index) => calculateTotal(index));
    const maxScore = Math.max(...totals);
    const winnerIndices = totals
      .map((score, index) => (score === maxScore ? index : -1))
      .filter((index) => index !== -1);

    return winnerIndices;
  };

  const winnerIndices = determineWinner();

  // Get the row name based on index
  const getRowName = (index: number) => {
    switch (index) {
      case 0:
        return "Money";
      case 1:
        return "Buildings";
      case 2:
        return "Trading Posts";
      case 3:
        return "Harbors";
      case 4:
        return "Pathfinder";
      case 5:
        return "Bonus and Hazards";
      case 6:
        return "Cards";
      case 7:
        return "Objectives";
      case 8:
        return "Harbormasters";
      case 9:
        return "Board";
      case 10:
        return "Game End";
      default:
        return `Unknown`;
    }
  };

  // Get current leader(s) names
  const getLeaders = () => {
    if (winnerIndices.length === 0) return "";

    const leaderNames = winnerIndices.map((index) => players[index]);
    if (leaderNames.length === 1) {
      return `${leaderNames[0]} is leading`;
    } else {
      return `${leaderNames.join(" and ")} are tied`;
    }
  };

  return (
    <div className="score-grid">
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Category</th>
              {players.map((player, index) => (
                <th
                  key={index}
                  className={
                    winnerIndices.includes(index) ? "winning-player" : ""
                  }
                >
                  {player}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {scores.map((row, rowIndex) => (
              <tr key={rowIndex}>
                <td>{getRowName(rowIndex)}</td>
                {players.map((_, playerIndex) => (
                  <td key={playerIndex}>
                    <input
                      type="number"
                      value={row[playerIndex] || ""}
                      onChange={(e) =>
                        handleScoreChange(rowIndex, playerIndex, e.target.value)
                      }
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                  </td>
                ))}
              </tr>
            ))}
            <tr className="total-row">
              <td>
                <strong>Total</strong>
              </td>
              {players.map((_, playerIndex) => (
                <td
                  key={playerIndex}
                  className={
                    winnerIndices.includes(playerIndex) ? "winning-player" : ""
                  }
                >
                  <strong>{calculateTotal(playerIndex)}</strong>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {winnerIndices.length > 0 && (
        <div className="leader-status">
          {getLeaders()} with {calculateTotal(winnerIndices[0])} points
        </div>
      )}

      <div className="buttons">
        <button onClick={onReset} className="reset-btn">
          Reset Game
        </button>
      </div>
    </div>
  );
};

export default ScoreGrid;
